import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSessionUser } from "@/lib/auth-session";
import { showAchievementToast } from "@/components/gamification/AchievementToast";

export interface LevelDefinition {
  id: string;
  level: number;
  title: string;
  icon: string;
  color: string;
  xp_required: number;
  credit_reward: number;
}

export interface AchievementDefinition {
  id: string;
  key: string;
  title: string;
  description: string;
  icon: string;
  category: string;
  tier: string;
  xp_reward: number;
  credit_reward: number;
  threshold: number;
  stat_key: string;
  is_hidden: boolean;
  sort_order: number;
}

export interface UserAchievement {
  id: string;
  achievement_id: string;
  earned_at: string;
  progress: number;
}

export interface UserXP {
  total_xp: number;
  current_level: number;
}

export function useGamification(userId?: string) {
  const [levels, setLevels] = useState<LevelDefinition[]>([]);
  const [achievements, setAchievements] = useState<AchievementDefinition[]>([]);
  const [userAchievements, setUserAchievements] = useState<UserAchievement[]>([]);
  const [userXP, setUserXP] = useState<UserXP>({ total_xp: 0, current_level: 1 });
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  const load = useCallback(async () => {
    const targetUserId = userId || (await getSessionUser())?.id;
    if (!targetUserId) { if (isMounted.current) setLoading(false); return; }

    const [levelsRes, achievementsRes, userAchRes, xpRes] = await Promise.all([
      supabase.from("level_definitions" as any).select("*").order("level"),
      supabase.from("achievement_definitions" as any).select("*").order("sort_order"),
      supabase.from("user_achievements" as any).select("*").eq("user_id", targetUserId),
      supabase.from("user_xp" as any).select("*").eq("user_id", targetUserId).maybeSingle(),
    ]);

    if (!isMounted.current) return;
    setLevels((levelsRes.data as any[]) || []);
    setAchievements((achievementsRes.data as any[]) || []);
    setUserAchievements((userAchRes.data as any[]) || []);
    if (xpRes.data) setUserXP(xpRes.data as any);
    setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const currentLevelDef = levels.find(l => l.level === userXP.current_level);
  const nextLevelDef = levels.find(l => l.level === userXP.current_level + 1);
  const xpProgress = nextLevelDef
    ? ((userXP.total_xp - (currentLevelDef?.xp_required || 0)) /
       (nextLevelDef.xp_required - (currentLevelDef?.xp_required || 0))) * 100
    : 100;

  const earnedIds = new Set(
    userAchievements.filter(ua => ua.progress >= (achievements.find(a => a.id === ua.achievement_id)?.threshold || 1))
      .map(ua => ua.achievement_id)
  );

  return {
    levels, achievements, userAchievements, userXP,
    currentLevelDef, nextLevelDef, xpProgress: Math.min(100, Math.max(0, xpProgress)),
    earnedIds, loading, reload: load,
  };
}

/** Grant a single achievement from client side (calls RPC) */
export async function grantAchievement(achievementKey: string, progress = 1) {
  const user = await getSessionUser();
  if (!user) return null;
  const { data, error } = await supabase.rpc("grant_achievement" as any, {
    _user_id: user.id,
    _achievement_key: achievementKey,
    _progress: progress,
  });
  if (error) console.error("Grant achievement error:", error);
  if (data?.earned === true) showAchievementToast(data);
  return data;
}

/**
 * Grant multiple achievements in one shot — calls auth.getUser() once and
 * fires all RPCs in parallel via Promise.allSettled.
 *
 * Each entry is either a plain key string (progress defaults to 1) or a
 * [key, progress] tuple for achievements that track cumulative progress.
 *
 * Replaces the pattern of N consecutive `grantAchievement(key).catch(()=>{})` calls
 * (which each re-fetch the auth session) with a single coalesced fan-out.
 */
export async function grantAchievements(
  keys: Array<string | [string, number]>
): Promise<void> {
  if (keys.length === 0) return;
  const user = await getSessionUser();
  if (!user) return;
  await Promise.allSettled(
    keys.map(async (entry) => {
      const [key, progress] = Array.isArray(entry) ? entry : [entry, 1];
      const { data, error } = await supabase.rpc("grant_achievement" as any, {
        _user_id: user.id,
        _achievement_key: key,
        _progress: progress,
      });
      if (error) console.error("Grant achievement error:", error);
      if (data?.earned === true) showAchievementToast(data);
    })
  );
}
